
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../../redux/actions/locationActions";
import { fetchDesignations } from "../../redux/actions/designationActions";
import theme from "../../components/shared/theme";
import {
  modalOverlayStyle,
  modalContentStyle,
  closeButtonStyle,
  inputStyle,
} from "../../components/shared/themeStyles";

const EditEmployeeModal = ({ employee, onChange, onCancel, onSubmit }) => {
  const dispatch = useDispatch();
  const { districts, municipalities } = useSelector((state) => state.location);
  const { designations } = useSelector((state) => state.designation);

  const [locationErrors, setLocationErrors] = useState({
    district: "",
    municipality: "",
  });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());

    if (employee?.assigned_district_ids && !employee.district_ids) {
      onChange("district_ids", employee.assigned_district_ids);
    }
    if (employee?.assigned_municipality_ids && !employee.municipality_ids) {
      onChange("municipality_ids", employee.assigned_municipality_ids);
    }
  }, [dispatch, employee]);

  const handleLocationSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;
    const errors = { district: "", municipality: "" };

    if (!employee.district_ids || employee.district_ids.length === 0) {
      errors.district = "Please select at least one district.";
      hasError = true;
    }

    if (!employee.municipality_ids || employee.municipality_ids.length === 0) {
      errors.municipality = "Please select at least one municipality.";
      hasError = true;
    }

    setLocationErrors(errors);
    if (hasError) return;

    const token = localStorage.getItem("token");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/assignUserLocation`,
        {
          user_id: employee.id,
          district_ids: employee.district_ids || [],
          municipality_ids: employee.municipality_ids || [],
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Locations assigned successfully");
    } catch (error) {
      console.error("Location error:", error);
      toast.error("Failed to assign locations");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const token = localStorage.getItem("token");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/employee-reset-password`,
        {
          id: employee.id,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Password reset successfully");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error("Failed to reset password");
    }
  };

  const filteredMunicipalities = useMemo(() => {
    if (!employee.district_ids || employee.district_ids.length === 0) return [];
    return municipalities.filter((muni) =>
      employee.district_ids.includes(muni.district_id)
    );
  }, [municipalities, employee.district_ids]);

  if (!employee) return null;

  return (
    <div
      style={{
        ...modalOverlayStyle,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          ...modalContentStyle,
          maxHeight: "90vh",
          overflowY: "auto",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <button onClick={onCancel} style={closeButtonStyle}>
          &times;
        </button>

        <h2
          style={{
            marginBottom: theme.spacing.md,
            fontSize: theme.font.size.xl,
            fontWeight: theme.font.weight.bold,
            color: theme.colors.grayDark,
          }}
        >
          Update Employee
        </h2>

        {/* Update Form */}
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <input
              style={inputStyle}
              type="text"
              value={employee.name}
              onChange={(e) => onChange("name", e.target.value)}
              placeholder="Name"
            />
            <input
              style={inputStyle}
              type="text"
              value={employee.email}
              onChange={(e) => onChange("email", e.target.value)}
              placeholder="Email"
            />
            <input
              style={inputStyle}
              type="text"
              value={employee.phone}
              onChange={(e) => onChange("phone", e.target.value)}
              placeholder="Phone"
            />
            <input
              style={inputStyle}
              type="date"
              value={employee.dob?.split("T")[0] || ""}
              onChange={(e) => onChange("dob", e.target.value)}
            />
            <input
              style={inputStyle}
              type="date"
              value={employee.doj?.split("T")[0] || ""}
              onChange={(e) => onChange("doj", e.target.value)}
            />

            <select
              style={inputStyle}
              value={employee.designation}
              onChange={(e) => onChange("designation", e.target.value)}
            >
              <option value="">Select Designation</option>
              {designations?.map((des) => (
                <option key={des} value={des}>
                  {des}
                </option>
              ))}
            </select>

            <select
              style={inputStyle}
              value={employee.district_id || ""}
              onChange={(e) => {
                const selectedId = parseInt(e.target.value);
                const selected = districts.find((d) => d.id === selectedId);
                onChange("district_id", selectedId);
                onChange("district", selected?.name || "");
              }}
            >
              <option value="">Select District</option>
              {districts?.map((dist) => (
                <option key={dist.id} value={dist.id}>
                  {dist.name}
                </option>
              ))}
            </select>

            <select
              style={inputStyle}
              value={employee.municipality_id || ""}
              onChange={(e) => {
                const selectedId = parseInt(e.target.value);
                const selected = municipalities.find(
                  (m) => m.id === selectedId
                );
                onChange("municipality_id", selectedId);
                onChange("municipality", selected?.name || "");
              }}
            >
              <option value="">Select Municipality</option>
              {municipalities?.map((muni) => (
                <option key={muni.id} value={muni.id}>
                  {muni.name}
                </option>
              ))}
            </select>

            <input
              style={inputStyle}
              type="text"
              value={employee.ward}
              onChange={(e) => onChange("ward", e.target.value)}
              placeholder="Ward"
            />
          </div>

          <div
            style={{
              marginTop: theme.spacing.lg,
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
              gap: theme.spacing.sm,
            }}
          >
            <button
              type="submit"
              style={{
                backgroundColor: theme.colors.primary,
                color: "#fff",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
              }}
            >
              Update
            </button>
            <button
              type="button"
              onClick={onCancel}
              style={{
                backgroundColor: "#eee",
                color: "#000",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Assign Locations Form */}
        <form onSubmit={handleLocationSubmit} style={{ marginTop: "2rem" }}>
          <h3 style={{ marginBottom: "0.5rem" }}>Assign Locations</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            {/* Districts Multi-Select */}
            <div>
              <label
                style={{
                  fontWeight: "bold",
                  marginBottom: "0.3rem",
                  display: "block",
                  color: theme.colors.grayDark,
                }}
              >
                Districts
              </label>
              <select
                multiple
                style={inputStyle}
                value={employee.district_ids || []}
                onChange={(e) => {
                  const selected = Array.from(
                    e.target.selectedOptions,
                    (opt) => parseInt(opt.value)
                  );
                  onChange("district_ids", selected);
                  setLocationErrors((prev) => ({ ...prev, district: "" }));
                }}
              >
                <option value="" disabled>
                  Select Districts
                </option>
                {districts?.map((dist) => (
                  <option key={dist.id} value={dist.id}>
                    {dist.name}
                  </option>
                ))}
              </select>
              {locationErrors.district && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.85rem",
                    marginTop: "0.25rem",
                  }}
                >
                  {locationErrors.district}
                </div>
              )}
            </div>

            {/* Municipalities Multi-Select */}
            <div>
              <label
                style={{
                  fontWeight: "bold",
                  marginBottom: "0.3rem",
                  display: "block",
                  color: theme.colors.grayDark,
                }}
              >
                Municipalities
              </label>
              <select
                multiple
                style={inputStyle}
                value={employee.municipality_ids || []}
                onChange={(e) => {
                  const selected = Array.from(
                    e.target.selectedOptions,
                    (opt) => parseInt(opt.value)
                  );
                  onChange("municipality_ids", selected);
                  setLocationErrors((prev) => ({
                    ...prev,
                    municipality: "",
                  }));
                }}
              >
                <option value="" disabled>
                  Select Municipalities
                </option>
                {filteredMunicipalities.map((muni) => (
                  <option key={muni.id} value={muni.id}>
                    {muni.name}
                  </option>
                ))}
              </select>
              {locationErrors.municipality && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.85rem",
                    marginTop: "0.25rem",
                  }}
                >
                  {locationErrors.municipality}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: theme.spacing.md,
              display: "flex",
              justifyContent: "flex-end",
              gap: theme.spacing.sm,
            }}
          >
            <button
              type="submit"
              style={{
                backgroundColor: "#28a745",
                color: "#fff",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
              }}
            >
              Assign Locations
            </button>
          </div>
        </form>

        {/* Reset Password Form */}
        <form onSubmit={handleResetPassword} style={{ marginTop: "2rem" }}>
          <h3 style={{ marginBottom: "0.5rem" }}>Reset Password</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <input
              style={inputStyle}
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <input
              style={inputStyle}
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: theme.spacing.md,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="submit"
              style={{
                backgroundColor: "#ffc107",
                color: "#000",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
              }}
            >
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
